/* Handling user state such as team and team user informations etc. */
import {
  GET_CURRENT_USER_ID,
  GET_TEAM_USERS_LIST_SUCCESS,
  GET_ALL_TEAM_USERS_LIST_SUCCESS,
  GET_USER_LIST_SUCCESS,
  GET_TEAM_DETAILS_SUCCESS,
  STORE_TEAM_DETAILS_SUCCESS,
  RESET_TEAM_DETAILS,
  ARCHIVE_TEAM_SUCCESS,
  STORE_TEAM_MEMBERS_SUCCESS
} from '@stores/mutation-types';
import {
  Team
} from '@services/App/team';
import store from '../../store';
import { TEAM_USER_ROLE } from '@constants';

const state = () => ({
  // User ID of the current user logged in
  currentUser: null,

  // List of teams the logged in user belongs to
  userTeams: {
    adminRole: null,
    leaderRole: null,
    memberRole: null,
    all: null
  },

  // Team user details
  team: {
    details: null,
    pagination: null,
    teamId: null
  },

  // User list
  user: {
    searchString: null,
    list: null
  },

  // Team details
  teamDetails: null,

  // API responses
  responses: {
    archive: null,
    store: null
  }
});

const mutations = {
  [RESET_TEAM_DETAILS]: (state) => {
    state.userTeams = {
      adminRole: null,
      leaderRole: null,
      memberRole: null,
      all: null
    };
    state.team = {
      details: null,
      pagination: null,
      teamId: null
    };
  },

  [GET_CURRENT_USER_ID]: (state, userId) => {
    state.currentUser = userId;
  },

  [GET_TEAM_USERS_LIST_SUCCESS]: (state, payload) => {
    if (!payload) {
      state.userTeams = {
        adminRole: null,
        leaderRole: null,
        memberRole: null,
        all: null
      };

      return;
    }
    
    state.userTeams.all = payload;
    state.userTeams.adminRole = payload.filter((team) => {
      return team.role === TEAM_USER_ROLE.ADMIN;
    });
    state.userTeams.leaderRole = payload.filter((team) => {
      return team.role === TEAM_USER_ROLE.LEADER;
    });
    state.userTeams.memberRole = payload.filter((team) => {
      return team.role === TEAM_USER_ROLE.MEMBER;
    });
  },

  [GET_ALL_TEAM_USERS_LIST_SUCCESS]: (state, payload) => {
    const { data, pagination } = payload;
    state.team.details = data ? data : [];
    state.team.pagination = pagination ? pagination : [];
  },

  [GET_USER_LIST_SUCCESS]: (state, payload) => {
    state.user.list = payload ? payload : [];
  },

  [GET_TEAM_DETAILS_SUCCESS]: (state, payload) => {
    state.teamDetails = payload;
  },

  [STORE_TEAM_DETAILS_SUCCESS]: (state, payload) => {
    state.responses.store = payload;
  },

  [ARCHIVE_TEAM_SUCCESS]: (state, payload) => {
    state.responses.archive = payload;
  },

  [STORE_TEAM_MEMBERS_SUCCESS]: (state, payload) => {
    state.responses.store = payload;
  }
};

const actions = {
  /**
   * Get list of teams the user belongs to
   *
   * @param {object} context Vuex context keys
   *
   * @return {promise}
   */
  async listTeamUsers({ commit, state }, archive = false) {
    if (state.userTeams.all && store.state.userId === state.currentUser) {
      return state.userTeams;
    }

    const response = await Team.listTeamUsers({
      userId: store.state.userId,
      archive
    });

    if (response.success) {
      commit(GET_TEAM_USERS_LIST_SUCCESS, response.data);
      commit(GET_CURRENT_USER_ID, store.state.userId);
    }

    return state.userTeams;
  },

  /**
   * Get team users
   *
   * @param {object} context Vuex context keys
   * @param {object} params of the endpoint
   *
   * @return {promise}
   */
  async getTeamUsers({ commit, state }, params) {
    if (
      params.search === null
      && state.team.teamId === params.teamId
      && state.team.pagination 
      && state.team.pagination.current_page === params.page
    ) {
      return state.team;
    }

    const response = await Team.listTeamUsers(params);

    if (response.success) {
      state.team.teamId = params.teamId;
      commit(GET_ALL_TEAM_USERS_LIST_SUCCESS, response);
    }

    return state.team;
  },

  /**
   * Search user to add
   *
   * @param {object} context Vuex context keys
   *
   * @return {promise}
   */
   async searchUserToAdd({ commit, state }, params) {
    if (state.user.searchString === params.search) {
      return state.user;
    }

    const response = await Team.listTeamUsers(params);
    if (response.success) {
      state.user.searchString = params.search
      commit(GET_USER_LIST_SUCCESS, response.data.data);
    }

    return state.user;
  },

  /**
   * Get team users
   *
   * @param {object} context Vuex context keys
   * @param {string} team ID
   *
   * @return {promise}
   */
  async getTeamDetails({ commit, state }, teamId) {
    const response = await Team.getTeamDetails(teamId);

    if (response.success) {
      commit(GET_TEAM_DETAILS_SUCCESS, response.data);
    }

    return state.teamDetails;
  },

  /**
   * Archive team
   *
   * @param {object} context Vuex context keys
   * @param {string} parameters
   *
   * @return {promise}
   */
  async archiveTeam({ commit, state }, params) {
    const response = await Team.archive(params);

    commit(ARCHIVE_TEAM_SUCCESS, response);

    return response;
  },

  /**
   * Store/Update team details
   *
   * @param {object} context Vuex context keys
   * @param {object} parameters
   *
   * @return {promise}
   */
  async saveTeam({ commit, state }, params) {
    const response = await Team.saveTeam(
      params,
      params.get('team_id') ? params.get('team_id') : null
    );

    commit(STORE_TEAM_DETAILS_SUCCESS, response);

    return response;
  },

  /**
   * Store/Update team members
   *
   * @param {object} context Vuex context keys
   * @param {object} parameters
   *
   * @return {promise}
   */
   async bulkAddTeamMembers({ commit, state }, members) {
    const response = await Team.bulkAddTeamMembers({
      admin_id: state.currentUser,
      team_id: state.team.teamId,
      ...members,
    });

    commit(STORE_TEAM_MEMBERS_SUCCESS, response);

    return response;
  }
};

const getters = {};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
